import { useAtomValue, useSetAtom } from "jotai";
import { loadable } from "jotai/utils";
import { useMemo } from "react";

import { $publishBranchCommit } from "../../internal/git";
import { useGitBranchAheadBehind } from "./useGitBranchAheadBehind";
import { useGitBranches } from "./useGitBranches";
import { useGitHistory } from "./useGitHistory";
import { useGitStatus } from "./useGitStatus";

/**
 * Commit graph entry type for timeline visualization
 */
export type CommitGraphEntry = {
	hash: string;
	parents: string[];
	message: string;
	author: string;
	date: string;
	/** JJ change ID (revision ID). Only present for JJ-backed repos. */
	changeId?: string;
	/** Whether this commit has unresolved conflicts (JJ-specific). */
	hasConflict?: boolean;
	/** Whether this commit is immutable (ancestor of trunk/tags/untracked remote bookmarks). */
	immutable?: boolean;
	/** Whether this commit is empty (no changes vs parent). */
	isEmpty?: boolean;
};

/**
 * Full commit graph data structure for timeline visualization
 */
export type CommitGraph = {
	commits: CommitGraphEntry[];
	branches: Record<string, string>; // branch name -> commit hash
	remoteBranches: Record<string, string>; // remote branch name -> commit hash (e.g., "moment/main")
	head: string; // current HEAD hash
	currentBranch: string | null; // current checked out branch name
	isDirty: boolean; // true if working directory has uncommitted changes
};

/**
 * Hook to build a commit graph from git history, branches, and status.
 * Combines data from multiple git atoms into the format needed for BranchTimeline.
 *
 * @param documentId - The document ID
 * @returns The commit graph data and loading state
 */
export const useCommitGraph = (documentId: string | undefined) => {
	const { history, isLoading: historyLoading, isError: historyError } = useGitHistory(documentId);
	const {
		branches,
		isLoading: branchesLoading,
		isError: branchesError,
	} = useGitBranches(documentId);
	const {
		data: status,
		isLoading: statusLoading,
		isError: statusError,
	} = useGitStatus(documentId);

	// Get the publish branch commit (moment/main remote)
	const publishBranchAtom = $publishBranchCommit(documentId);
	const publishBranchLoadable = useAtomValue(loadable(publishBranchAtom));
	const publishBranchCommit =
		publishBranchLoadable.state === "hasData" ? publishBranchLoadable.data : undefined;
	const refreshPublishBranch = useSetAtom(publishBranchAtom);

	// Get ahead/behind commit lists for publish branch
	const {
		data: aheadBehindData,
		publishableAhead,
		refresh: refreshAheadBehind,
	} = useGitBranchAheadBehind(documentId ?? "", "main", "refs/remotes/moment/main");

	const graph = useMemo<CommitGraph | undefined>(() => {
		if (!history || !branches || !status) {
			return undefined;
		}

		// Transform commits to CommitGraphEntry format
		const commits: CommitGraphEntry[] = history.commits.map((c) => ({
			hash: c.hash,
			parents: c.parents,
			message: c.message,
			author: c.author,
			date: c.date,
			changeId: c.change_id,
			hasConflict: c.has_conflict,
			immutable: c.immutable,
			isEmpty: c.is_empty,
		}));

		// Transform branches to name -> commit hash map
		const branchMap: Record<string, string> = {};
		for (const branch of branches) {
			branchMap[branch.name] = branch.commitHash;
		}

		// Build remote branches map
		const remoteBranchMap: Record<string, string> = {};
		if (publishBranchCommit) {
			remoteBranchMap["moment/main"] = publishBranchCommit;
		}

		return {
			commits,
			branches: branchMap,
			remoteBranches: remoteBranchMap,
			head: status.commit,
			currentBranch: status.branch || null,
			isDirty: !status.isClean,
		};
	}, [history, branches, status, publishBranchCommit]);

	return {
		graph,
		aheadBehindData,
		publishableAhead,
		refreshAheadBehind,
		refreshPublishBranch,
		isLoading: historyLoading || branchesLoading || statusLoading,
		isError: historyError || branchesError || statusError,
	};
};
